// useIntegratedMenu.js — 통합 메뉴 CRUD API 훅 (hard delete 전환)
//   2026-10-01: enabled(운영자만 — 관리 주소는 그 외 사람에게 403), loaded(첫 목록 도착),
//   만들기·고치기·지우기 뒤 사이드바·작업 대상의 통합 메뉴 목록도 다시 받는다(같은 주소라 화면 이동으로는 안 받음).
//   2026-10-07: 편집 창의 회사 단추 = **작업 대상에서 고른 테넌트의 사용 중 회사**(통합 관리 홈과 같은 출처).
//     전엔 화면에 박아 둔 'PINE 파인그룹' + 운영자 자기 테넌트 외부회사였다 — 미팅링크에선 쓰지 않는 PINE 단추 하나만 떠서
//     아무도 못 여는 메뉴를 만들 수 있었다. 테넌트를 바꾸는 사이(목록 도착 전)에는 빈 목록이다(옛 테넌트 단추가 안 보이게).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useCompanyScope } from '../adminlayout/CompanyScopeContext';

const BASE = '/api/admin/integrated-menus';

// 2026-10-15: '통합 메뉴 관리' 열쇠 보유자(테넌트 관리자 등)도 쓴다. 서버 목록 응답에
//   allowed_features(고를 수 있는 기능 — 자기 테넌트 예산) · tenant_companies(자기 테넌트 회사·이름, 운영자는 null)가 온다.
//   열쇠 보유자의 회사 단추는 tenant_companies — 직원 화면의 회사 목록은 받은 열쇠가 있는 회사만이라 모자랄 수 있다.
export function useIntegratedMenu({ showToast, enabled = true }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [allowedFeatures, setAllowedFeatures] = useState(null); // null = 제한 없음(모름)
  const [tenantCompanies, setTenantCompanies] = useState(null); // null = 운영자(작업 대상 회사 목록을 쓴다)
  const scope = useCompanyScope();
  const refreshScope = scope?.refreshIntegratedMenus;
  const scopeReady = Boolean(scope?.companiesReady);
  const scopeCompanies = scope?.companies;
  const companiesReady = tenantCompanies ? true : scopeReady;

  // 회사 단추: 대표 회사 먼저, 나머지는 코드 순
  const companies = useMemo(() => {
    if (Array.isArray(tenantCompanies)) return tenantCompanies.map(({ code, name }) => ({ code, name: name || code }));
    if (!scopeReady || !Array.isArray(scopeCompanies)) return [];
    return scopeCompanies
      .map((c) => ({ code: c.code, name: c.name || c.code, primary: c.kind === 'primary' }))
      .sort((a, b) => Number(b.primary) - Number(a.primary) || String(a.code).localeCompare(String(b.code)))
      .map(({ code, name }) => ({ code, name }));
  }, [tenantCompanies, scopeReady, scopeCompanies]);

  // ── 목록 조회 (assignees_count 포함) ──────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(BASE);
      if (r.ok) {
        setList(r.data?.list || []);
        setAllowedFeatures(Array.isArray(r.data?.allowed_features) ? r.data.allowed_features : null);
        setTenantCompanies(Array.isArray(r.data?.tenant_companies) ? r.data.tenant_companies : null);
      } else showToast?.(r.data?.message || '목록 조회 실패', 'error');
    } finally { setLoading(false); setLoaded(true); }
  }, [showToast]);
  const afterChange = useCallback(async () => {
    await fetchList();
    refreshScope?.();
  }, [fetchList, refreshScope]);

  // ── CRUD ──────────────────────────────────────────────────────────────
  const create = useCallback(async (payload) => {
    const r = await apiClient.post(BASE, payload);
    if (r.ok) { showToast?.('통합 메뉴가 생성되었습니다.', 'success'); await afterChange(); return { ok: true, menu: r.data?.menu }; }
    showToast?.(r.data?.message || '생성 실패', 'error');
    return { ok: false, message: r.data?.message };
  }, [afterChange, showToast]);

  const update = useCallback(async (id, payload) => {
    const r = await apiClient.put(`${BASE}/${id}`, payload);
    if (r.ok) { showToast?.('수정되었습니다.', 'success'); await afterChange(); return { ok: true, menu: r.data?.menu }; }
    showToast?.(r.data?.message || '수정 실패', 'error');
    return { ok: false, message: r.data?.message };
  }, [afterChange, showToast]);

  // ── Hard delete ──────────────────────────────────────────────────────
  // assigneesCount는 호출 측에서 confirm 메시지에 표시
  const remove = useCallback(async (id, assigneesCount = 0) => {
    const msg = assigneesCount > 0
      ? `이 메뉴를 삭제하시겠습니까?\n\n부여받은 ${assigneesCount}명의 권한도 함께 제거됩니다.\n(이 작업은 되돌릴 수 없습니다)`
      : `이 메뉴를 삭제하시겠습니까?\n(이 작업은 되돌릴 수 없습니다)`;
    if (!window.confirm(msg)) return false;
    const r = await apiClient.del(`${BASE}/${id}`);
    if (r.ok) { showToast?.('삭제되었습니다.', 'success'); await afterChange(); return true; }
    showToast?.(r.data?.message || '삭제 실패', 'error');
    return false;
  }, [afterChange, showToast]);

  // ── 초기 로드 ─────────────────────────────────────────────────────────
  useEffect(() => { if (enabled) fetchList(); }, [enabled, fetchList]);

  return { list, companies, companiesReady, allowedFeatures, loading, loaded, fetchList, create, update, remove };
}
